import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { PremiumPageHeader } from '@repo/ui';
import {
  LayoutDashboard,
  Users2,
  ClipboardCheck,
  TicketCheck,
  CalendarClock,
  Clock,
  Video,
  Wifi,
  WifiOff,
  BookOpen,
  Zap,
  ExternalLink,
  Calendar,
  ArrowUpRight,
  User,
  MoreVertical,
} from 'lucide-react';

// ── helpers ──────────────────────────────────────────────────────────────────

function formatTime(t: string | Date | undefined, lang: string) {
  if (!t) return null;

  let date: Date;
  if (typeof t === 'string' && t.includes(':')) {
    const [hStr, mStr] = t.split(':');
    date = new Date();
    date.setHours(parseInt(hStr, 10), parseInt(mStr, 10), 0, 0);
  } else {
    date = new Date(t);
  }

  const h = date.getHours();
  const m = date.getMinutes();

  if (lang === 'ar') {
    const ampm = h >= 12 ? 'م' : 'ص';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  }

  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatSessionDate(dateStr: string | undefined, lang: string) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// ── component ─────────────────────────────────────────────────────────────────

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const { data, isLoading } = useQuery({
    queryKey: ['student-dashboard'],
    queryFn: () => api.get('/student/dashboard').then((res) => res.data?.data),
    refetchInterval: 30_000, // refresh every 30s to pick up newly-started meetings
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-primary/20 animate-spin border-t-primary" />
          <LayoutDashboard className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
        </div>
        <p className="text-muted-foreground font-medium animate-pulse">
          {t('student.dashboard.loading', { defaultValue: 'Preparing dashboard...' })}
        </p>
      </div>
    );
  }

  const activeSessions: any[] = data?.activeSessions ?? [];
  const nextSession = data?.upcomingSession;

  const stats = [
    {
      label: t('student.dashboard.enrolledGroups'),
      value: data?.enrolledGroups ?? 0,
      icon: Users2,
      color: 'text-indigo-600',
      bg: 'bg-indigo-500/10',
      border: 'border-indigo-100 dark:border-indigo-900/50',
      gradient: 'from-indigo-500/5 to-transparent',
    },
    {
      label: t('student.dashboard.attendanceRate'),
      value: `${data?.attendanceSummary?.rate ?? data?.attendanceRate ?? 0}%`,
      icon: ClipboardCheck,
      color: 'text-emerald-600',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-100 dark:border-emerald-900/50',
      gradient: 'from-emerald-500/5 to-transparent',
    },
    {
      label: t('student.dashboard.openTickets'),
      value: data?.openTickets ?? 0,
      icon: TicketCheck,
      color: 'text-amber-600',
      bg: 'bg-amber-500/10',
      border: 'border-amber-100 dark:border-amber-900/50',
      gradient: 'from-amber-500/5 to-transparent',
    },
  ];

  const getSessionTimes = (session: any) => {
    const st = session.scheduledTime;
    if (st?.scheduledStartDate) {
      const start = formatTime(new Date(st.scheduledStartDate), i18n.language);
      const end = st.scheduledEndDate
        ? formatTime(new Date(st.scheduledEndDate), i18n.language)
        : null;
      return { start, end };
    }
    return { start: null, end: null };
  };

  return (
    <div className="space-y-8 pb-12 overflow-x-hidden">
      {/* ── Standardized Header ── */}
      <PremiumPageHeader
        title={t('student.dashboard.title')}
        subtitle={t('student.auth.heroSubtitle')}
        icon={LayoutDashboard}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {/* ── Stats Grid ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className={`group relative overflow-hidden rounded-2xl border ${stat.border} bg-card p-6 transition-all hover:shadow-xl hover:-translate-y-1 duration-300`}
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity`}
            />
            <div className="relative z-10 flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-bold text-muted-foreground ">{stat.label}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black transition-transform group-hover:scale-110 duration-300">
                    {stat.value}
                  </span>
                </div>
              </div>
              <div
                className={`rounded-xl ${stat.bg} p-4 text-2xl transition-all group-hover:rotate-12 duration-300`}
              >
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </div>
            <div className="relative z-10 mt-4 flex items-center text-xs font-bold text-muted-foreground/60 transition-colors group-hover:text-primary">
              <span className="">{t('common.viewDetails', { defaultValue: 'View Details' })}</span>
              <ArrowUpRight className={`h-4 w-4 ${isRtl ? 'mr-1' : 'ml-1'}`} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* ── Current/Ongoing Sessions ──────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
                <div className="relative h-3 w-3 rounded-full bg-primary ring-4 ring-primary/10" />
              </div>
              <h2 className="text-xl font-black tracking-tight">
                {t('student.dashboard.todaySessions')}
              </h2>
            </div>
            {activeSessions.length > 0 && (
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary border border-primary/20">
                {activeSessions.length} {t('common.active', { defaultValue: 'Active' })}
              </span>
            )}
          </div>

          <div className="space-y-4">
            {activeSessions.length > 0 ? (
              activeSessions.map((session: any) => {
                const isOngoing =
                  session.isOngoing || session.status === 'ongoing' || session.status === 'started';
                const isOnline = session.deliveryMode === 'online';
                const hasJoinUrl = isOnline && !!session.joinUrl;
                const groupNames =
                  session.groups?.length > 0
                    ? session.groups.map((g: any) => g.name).join(', ')
                    : session.sessionFormula === 'workshop-exception'
                      ? t('student.dashboard.workshopException')
                      : t('student.dashboard.workshop', { defaultValue: 'Workshop' });

                const { start: startTime, end: endTime } = getSessionTimes(session);
                const sessionDateStr = formatSessionDate(session.sessionDate, i18n.language);
                const hostName = session.host
                  ? `${session.host.firstName ?? ''} ${session.host.lastName ?? ''}`.trim()
                  : null;

                return (
                  <div
                    key={session._id}
                    className={`group relative overflow-hidden rounded-3xl border-2 p-6 transition-all duration-300 shadow-sm hover:shadow-xl ${
                      isOngoing
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-border bg-card hover:border-primary/20'
                    }`}
                  >
                    {isOngoing && (
                      <div className="absolute top-0 right-0 p-4">
                        <span className="flex items-center gap-1.5 rounded-full bg-red-500/10 px-2.5 py-1 text-[10px] font-black  text-red-600 border border-red-500/20 shadow-sm animate-pulse">
                          <span className="h-1.5 w-1.5 rounded-full bg-red-600" />
                          {t('common.live', { defaultValue: 'Live Now' })}
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col md:flex-row gap-6">
                      <div
                        className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl transition-all duration-300 group-hover:scale-110 shadow-inner ${
                          isOngoing ? 'bg-primary/20' : 'bg-muted'
                        }`}
                      >
                        {session.type === 'workshop' ? (
                          <Zap
                            className={`h-8 w-8 ${isOngoing ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        ) : (
                          <BookOpen
                            className={`h-8 w-8 ${isOngoing ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        )}
                      </div>

                      <div className="flex-1 space-y-4 min-w-0 text-start">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-black  text-primary border border-primary/20">
                              {t(`student.dashboard.${session.type}`)}
                            </span>
                          </div>
                          <h3 className="text-xl font-black tracking-tight truncate leading-tight transition-colors group-hover:text-primary">
                            {groupNames}
                          </h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                          <div className="flex items-center gap-2.5 text-sm text-muted-foreground font-medium">
                            <Clock className="h-4 w-4 text-primary/60" />
                            <span>
                              {startTime}
                              {endTime ? ` — ${endTime}` : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2.5 text-sm text-muted-foreground font-medium">
                            <Calendar className="h-4 w-4 text-primary/60" />
                            <span>{sessionDateStr}</span>
                          </div>
                          {hostName && (
                            <div className="flex items-center gap-2.5 text-sm text-muted-foreground font-medium">
                              <User className="h-4 w-4 text-primary/60" />
                              <span className="truncate">{hostName}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2.5 text-sm font-bold tracking-wide">
                            {isOnline ? (
                              <div className="flex items-center gap-2 text-indigo-600">
                                <Wifi className="h-4 w-4" />
                                <span>{t('student.dashboard.online')}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <WifiOff className="h-4 w-4" />
                                <span>{t('student.dashboard.offline')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col justify-center items-center md:items-end gap-3 min-w-[140px]">
                        {hasJoinUrl ? (
                          <a
                            href={session.joinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3 text-sm font-black text-primary-foreground shadow-xl shadow-primary/30 transition-all hover:scale-105 hover:bg-primary/90 active:scale-95"
                          >
                            <Video className="h-4 w-4" />
                            {t('student.dashboard.joinMeeting')}
                            <ExternalLink className="h-3.5 w-3.5 opacity-50" />
                          </a>
                        ) : isOnline ? (
                          <div className="flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500/10 px-6 py-3 text-sm font-black text-indigo-600 border border-indigo-500/20">
                            <Wifi className="h-4 w-4 animate-pulse" />
                            {t('student.dashboard.onlineSession')}
                          </div>
                        ) : null}

                        <button className="p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors md:hidden">
                          <MoreVertical className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center p-12 rounded-3xl border border-dashed bg-muted/30 space-y-4">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                  <CalendarClock className="h-8 w-8 text-muted-foreground/40" />
                </div>
                <div className="text-center space-y-1">
                  <p className="font-bold text-muted-foreground">
                    {t('student.dashboard.noUpcoming')}
                  </p>
                  <p className="text-xs text-muted-foreground/60">
                    {t('common.checkBackLater', {
                      defaultValue: 'Check back later for newly started sessions.',
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Next Session Card ───────────────────────────────────────────── */}
        <div className="space-y-6">
          <h2 className="text-xl font-black tracking-tight px-2 flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" />
            {t('student.dashboard.nextSession')}
          </h2>

          <div
            className={`relative overflow-hidden rounded-3xl border border-border bg-card p-6 shadow-xl transition-all hover:shadow-2xl hover:border-primary/20 group`}
          >
            <div className="absolute top-0 right-0 h-32 w-32 -translate-y-16 translate-x-16 rounded-full bg-primary/5 transition-transform group-hover:scale-150 duration-500" />

            {nextSession ? (
              <div className="space-y-6 relative z-10">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-muted px-2 py-0.5 text-[9px] font-black  text-muted-foreground border border-border">
                      {t(`student.dashboard.${nextSession.type}`)}
                    </span>
                    {nextSession.deliveryMode === 'online' && (
                      <span className="rounded-md bg-indigo-500/10 px-2 py-0.5 text-[9px] font-black  text-indigo-600 border border-indigo-500/20 flex items-center gap-1">
                        <Wifi className="h-2.5 w-2.5" />
                        Online
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-black leading-tight tracking-tight">
                    {nextSession.groups?.length > 0
                      ? nextSession.groups.map((g: any) => g.name).join(', ')
                      : nextSession.sessionFormula === 'workshop-exception'
                        ? t('student.dashboard.workshopException')
                        : t('student.dashboard.workshop', { defaultValue: 'Workshop' })}
                  </h3>
                </div>

                <div className="space-y-3 rounded-2xl bg-muted/40 p-4">
                  <div className="flex items-center gap-3 text-sm font-bold text-foreground/80">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span>
                      {new Date(
                        nextSession.sessionDate || nextSession.startDate
                      ).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm font-bold text-foreground/80">
                    <Clock className="h-4 w-4 text-primary" />
                    <span>
                      {getSessionTimes(nextSession).start}
                      {getSessionTimes(nextSession).end && ` — ${getSessionTimes(nextSession).end}`}
                    </span>
                  </div>
                  {nextSession.host && (
                    <div className="flex items-center gap-3 text-sm font-bold text-foreground/80">
                      <User className="h-4 w-4 text-primary" />
                      <span className="truncate">
                        {nextSession.host.firstName} {nextSession.host.lastName}
                      </span>
                    </div>
                  )}
                </div>

                <button className="w-full rounded-2xl bg-muted py-3 text-sm font-black text-muted-foreground transition-all hover:bg-primary hover:text-primary-foreground">
                  {t('common.setReminder', { defaultValue: 'Remind Me' })}
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-muted-foreground/20" />
                </div>
                <p className="text-sm font-bold text-muted-foreground">
                  {t('student.dashboard.noUpcoming')}
                </p>
              </div>
            )}
          </div>

          {/* ── Quick Links ──────────────────────────────────────────────── */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-lg space-y-4">
            <h3 className="text-sm font-black  text-muted-foreground/60">
              {t('common.quickLinks', { defaultValue: 'Quick Access' })}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: t('student.navigation.schedule'), icon: Calendar, color: 'text-blue-500' },
                {
                  label: t('student.navigation.attendance'),
                  icon: ClipboardCheck,
                  color: 'text-emerald-500',
                },
                { label: t('student.navigation.payments'), icon: Zap, color: 'text-amber-500' },
                {
                  label: t('student.navigation.tickets'),
                  icon: TicketCheck,
                  color: 'text-rose-500',
                },
              ].map((link, i) => (
                <button
                  key={i}
                  className="flex flex-col items-center justify-center p-4 rounded-2xl bg-muted/30 border border-transparent hover:border-primary/20 hover:bg-card transition-all group"
                >
                  <link.icon
                    className={`h-6 w-6 mb-2 ${link.color} transition-transform group-hover:scale-110`}
                  />
                  <span className="text-[10px] font-black text-center leading-tight">
                    {link.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
